import { z } from 'zod';
import type { SsnCountry } from '../enums.js';

/**
 * Contrat réseau du module RGPD : droit d'accès (export) et droit à l'effacement.
 *
 * Les deux sont **disponibles sur tous les plans**. La V1 conditionnait l'export au plan payant
 * (`requireRGPDExport`, `docs/audit/audit-security.md` §7.2) : un droit du patient ne peut pas
 * dépendre de l'abonnement de son praticien.
 */

export const patientExportQuerySchema = z
  .object({ format: z.enum(['json', 'pdf']).default('json') })
  .strict();
export type PatientExportQuery = z.infer<typeof patientExportQuerySchema>;

/**
 * Le nom du dossier, retapé. Une confirmation qui se contente d'un bouton s'appuie sur l'attention
 * de quelqu'un qui en a déjà cliqué cent ; retaper un nom est le seul geste qui ne se fait pas
 * distraitement — et l'effacement n'est pas réversible.
 */
export const patientEraseSchema = z
  .object({ confirmName: z.string().trim().min(1).max(200) })
  .strict();
export type PatientEraseInput = z.infer<typeof patientEraseSchema>;

export interface PatientExportIdentityDto {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  sex: string | null;
  ssn: string | null;
  /** Typé, et non texte libre comme ses voisins : l'impression groupe le numéro selon son pays. */
  ssnCountry: SsnCountry;
  profession: string | null;
  preferredLanguage: string | null;
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
  street: string | null;
  houseNumber: string | null;
  addressDetails: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  referralSource: string | null;
  familyStatus: string | null;
  numberOfChildren: number | null;
  primaryPhysician: string | null;
  companyName: string | null;
  vatNumber: string | null;
  notesAdmin: string | null;
  patientNote: string | null;
  sharingConsent: boolean;
  sharingConsentAt: string | null;
  acceptsAppointmentReminders: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PatientExportLifestyleDto {
  smokerStatus: string | null;
  smokerQuantity: number | null;
  smokerQuantityPeriod: string | null;
  alcoholConsumption: string | null;
  alcoholQuantity: number | null;
  alcoholQuantityPeriod: string | null;
  handPreference: string | null;
  referenceHeight: number | null;
  referenceWeight: number | null;
  physicalActivities: { activity: string; frequency: string }[];
  currentMedications: string | null;
  allergies: string | null;
}

export interface PatientExportHistoryDto {
  kind: string;
  category: string | null;
  systemType: string | null;
  title: string;
  description: string | null;
  date: string | null;
  dateReference: string | null;
  dateAmount: number | null;
  dateUnit: string | null;
  isImportant: boolean;
  isActive: boolean;
}

export interface PatientExportRelationshipDto {
  relationshipType: string;
  relatedPatientName: string;
  notes: string | null;
}

export interface PatientExportAppointmentDto {
  startsAt: string;
  endsAt: string;
  status: string;
  source: string;
  notes: string | null;
}

export interface PatientExportMeasurementDto {
  code: string;
  value: number;
}

export interface PatientExportTestDto {
  testCode: string;
  zone: string;
  result: string;
  notes: string | null;
}

/**
 * Libellé d'une question d'un questionnaire **du cabinet**. Les questionnaires intégrés n'en ont
 * pas besoin : leurs libellés vivent dans `@osteopro/clinical` et sont traduits à l'impression.
 * Ceux du cabinet sont du texte saisi : sans eux, `{"q1": 6}` ne dit rien au patient qui relit son
 * dossier, et l'export manquerait son but.
 */
export interface PatientExportQuestionnaireFieldDto {
  id: string;
  label: string;
}

export interface PatientExportQuestionnaireDto {
  kind: string;
  standardCode: string | null;
  score: number | null;
  interpretationKey: string | null;
  completedAt: string;
  answers: unknown;
  /** Nom du modèle du cabinet ; nul pour un questionnaire intégré. */
  templateName: string | null;
  /** Libellés des questions du modèle du cabinet ; vide pour un questionnaire intégré. */
  templateFields: PatientExportQuestionnaireFieldDto[];
}

/**
 * Schéma corporel d'une consultation. `drawingData` est le dessin lui-même, dans le format
 * documenté par `body-diagram.ts` : traits, formes, textes, et muscles surlignés en face et dos. Il part
 * dans l'export parce que c'est une donnée du patient — la portabilité ne se limite pas à ce que
 * le serveur sait interpréter.
 */
export interface PatientExportBodyDiagramDto {
  view: string;
  drawingData: Record<string, unknown>;
  updatedAt: string;
}

export interface PatientExportEncounterDto {
  startedAt: string;
  completedAt: string | null;
  status: string;
  title: string | null;
  anamnesis: string | null;
  observations: string | null;
  diagnosticEvolution: string | null;
  osteopathicTests: string | null;
  treatment: string | null;
  conclusion: string | null;
  advice: string | null;
  measurements: PatientExportMeasurementDto[];
  clinicalTests: PatientExportTestDto[];
  questionnaires: PatientExportQuestionnaireDto[];
  bodyDiagrams: PatientExportBodyDiagramDto[];
}

export interface PatientExportPaymentDto {
  method: string;
  /** Ce qui a été reçu. */
  amountCents: number;
  /** Part imputée aux intérêts de retard, comme `dont_interets` dans l'export comptable. */
  interestCents: number;
  receivedAt: string;
}

export interface PatientExportInvoiceDto {
  number: string | null;
  status: string;
  issueDate: string;
  dueDate: string | null;
  totalCents: number;
  paidCents: number;
  creditedCents: number;
  payments: PatientExportPaymentDto[];
}

/** Métadonnées seules : l'octet se télécharge par la route documents, pièce par pièce. */
export interface PatientExportDocumentDto {
  fileName: string;
  category: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  description: string | null;
  createdAt: string;
  deletedAt: string | null;
}

/**
 * Courrier médical, **corps compris**. Un courrier est une pièce du dossier autant qu'une
 * consultation : la V1 l'omettait de son export, si bien qu'une demande d'accès rendait un fichier
 * incomplet (`docs/audit/parite-v1-v2.md` §1.1.1). L'adresse réellement servie à l'envoi n'y figure
 * pas : elle concerne le correspondant, pas le patient.
 */
export interface PatientExportLetterDto {
  letterType: string;
  subject: string;
  recipientType: string;
  recipientName: string | null;
  body: string;
  status: string;
  /** « manual » ou « ai » : le patient a le droit de savoir ce qui a rédigé son courrier. */
  source: string;
  aiModel: string | null;
  sentAt: string | null;
  createdAt: string;
}

export interface PatientExportDto {
  exportedAt: string;
  organization: { id: string; name: string };
  patient: PatientExportIdentityDto;
  lifestyle: PatientExportLifestyleDto | null;
  history: PatientExportHistoryDto[];
  relationships: PatientExportRelationshipDto[];
  appointments: PatientExportAppointmentDto[];
  encounters: PatientExportEncounterDto[];
  invoices: PatientExportInvoiceDto[];
  documents: PatientExportDocumentDto[];
  medicalLetters: PatientExportLetterDto[];
}

/**
 * Export complet du cabinet (`POST /api/organizations/current/export`) : chaque dossier dans la
 * forme exacte de l'export unitaire. Propriétaire seul ; ouvert aussi à un cabinet bloqué.
 */
export interface OrganizationExportDto {
  exportedAt: string;
  organization: { id: string; name: string };
  patients: PatientExportDto[];
}

export interface PatientErasedDto {
  patientId: string;
  anonymizedAt: string;
}
